import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, RefreshCw, CheckCircle, Eye, ShieldOff, Trash2, MousePointerClick, TrendingUp, Percent } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminMarketingPanel({ token, isDarkMode }) {
  const [runningCampaign, setRunningCampaign] = useState(false);
  const [lastCampaignResult, setLastCampaignResult] = useState(null);
  const [campaignHistory, setCampaignHistory] = useState([]);
  const [campaignMetrics, setCampaignMetrics] = useState(null);
  const [campaignTrends, setCampaignTrends] = useState([]);
  const [suppressionEntries, setSuppressionEntries] = useState([]);
  const [suppressionEmail, setSuppressionEmail] = useState("");
  const [loadingLists, setLoadingLists] = useState(false);

  const fetchMarketingData = useCallback(async () => {
    if (!token) return;
    setLoadingLists(true);
    try {
      const [historyRes, suppressionRes, metricsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/admin/campaign-history`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/email-suppression`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/campaign-metrics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/campaign-trends`, { headers: { Authorization: `Bearer ${token}` }, params: { days: 14 } }),
      ]);
      setCampaignHistory(historyRes.data.campaigns || []);
      setSuppressionEntries(suppressionRes.data.entries || []);
      setCampaignMetrics(metricsRes.data || null);
      setCampaignTrends(trendsRes.data.trends || []);
    } catch (error) {
      toast.error("Failed to load marketing controls");
    } finally {
      setLoadingLists(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMarketingData();
  }, [fetchMarketingData]);

  const runWinbackCampaign = async (dryRun) => {
    if (!dryRun && !window.confirm("Send win-back emails to eligible users now?")) return;
    setRunningCampaign(true);
    try {
      const response = await axios.post(`${API}/admin/winback-campaign`, {}, {
        params: { dry_run: dryRun },
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastCampaignResult(response.data);
      toast.success(response.data.message);
      await fetchMarketingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to run campaign");
    } finally {
      setRunningCampaign(false);
    }
  };

  const addSuppression = async () => {
    if (!suppressionEmail.trim()) {
      toast.error("Enter an email to suppress");
      return;
    }
    try {
      await axios.post(`${API}/admin/email-suppression`, {
        email: suppressionEmail,
        reason: "admin_suppressed"
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Email suppressed");
      setSuppressionEmail("");
      await fetchMarketingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to suppress email");
    }
  };

  const removeSuppression = async (email) => {
    try {
      await axios.delete(`${API}/admin/email-suppression/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Suppression removed");
      await fetchMarketingData();
    } catch (error) {
      toast.error("Failed to remove suppression");
    }
  };

  const formatDate = (value) => value ? new Date(value).toLocaleString() : "N/A";

  return (
    <motion.div variants={null} className="glass rounded-2xl p-6 md:col-span-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20" data-testid="admin-marketing-panel">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h3 className="font-semibold flex items-center gap-2 text-purple-400">
          <Mail className="w-5 h-5" />
          Marketing Tools
        </h3>
        <Button variant="ghost" size="sm" onClick={fetchMarketingData} disabled={loadingLists} data-testid="refresh-marketing-data-button">
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingLists ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {campaignMetrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="campaign-performance-metrics">
          <div className="rounded-xl bg-white/5 p-3">
            <div className="flex items-center gap-2 text-xs text-white/45"><Mail className="w-3.5 h-3.5" />Sent</div>
            <p className="text-xl font-black" data-testid="campaign-metric-sent">{campaignMetrics.sent}</p>
          </div>
          <div className="rounded-xl bg-white/5 p-3">
            <div className="flex items-center gap-2 text-xs text-white/45"><Eye className="w-3.5 h-3.5" />Open rate</div>
            <p className="text-xl font-black text-cyan-300" data-testid="campaign-metric-open-rate">{campaignMetrics.open_rate}%</p>
          </div>
          <div className="rounded-xl bg-white/5 p-3">
            <div className="flex items-center gap-2 text-xs text-white/45"><MousePointerClick className="w-3.5 h-3.5" />Click rate</div>
            <p className="text-xl font-black text-pink-300" data-testid="campaign-metric-click-rate">{campaignMetrics.click_rate}%</p>
          </div>
          <div className="rounded-xl bg-white/5 p-3">
            <div className="flex items-center gap-2 text-xs text-white/45"><TrendingUp className="w-3.5 h-3.5" />Conversions</div>
            <p className="text-xl font-black text-green-300" data-testid="campaign-metric-conversions">{campaignMetrics.converted}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <Button onClick={() => runWinbackCampaign(true)} disabled={runningCampaign} className="bg-white/10 hover:bg-white/15 text-white" data-testid="preview-winback-campaign-button">
          <Eye className="w-4 h-4 mr-2" />
          Preview Targets
        </Button>
        <Button onClick={() => runWinbackCampaign(false)} disabled={runningCampaign} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white" data-testid="run-winback-campaign-button">
          {runningCampaign ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          {runningCampaign ? "Running..." : "Run Win-back Campaign"}
        </Button>
        {lastCampaignResult && (
          <div className="flex items-center gap-2 text-sm" data-testid="last-winback-campaign-result">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-white/60">{lastCampaignResult.dry_run ? "Preview" : "Last run"}: {lastCampaignResult.target_users} targets, {lastCampaignResult.emails_sent} sent</span>
          </div>
        )}
      </div>

      {campaignTrends.length > 0 && (
        <div className={`rounded-xl p-4 mb-4 ${isDarkMode ? "bg-black/20" : "bg-white/60"}`} data-testid="campaign-trend-chart">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-sm">14-Day Campaign Trend</h4>
            <span className="text-[10px] text-white/35">sent • opened • clicked • converted</span>
          </div>
          <div className="h-28 flex items-end gap-1.5">
            {campaignTrends.map((day) => {
              const max = Math.max(1, ...campaignTrends.map((entry) => Math.max(entry.sent, entry.opened, entry.clicked, entry.converted)));
              return (
                <div key={day.date} className="flex-1 h-full flex flex-col justify-end gap-1" data-testid={`campaign-trend-day-${day.date}`} title={`${day.date}: ${day.sent} sent, ${day.opened} opened, ${day.clicked} clicked, ${day.converted} converted`}>
                  <div className="grid grid-cols-4 gap-0.5 items-end h-full">
                    <span className="rounded-t bg-white/35" style={{ height: `${Math.max(2, (day.sent / max) * 90)}%` }} />
                    <span className="rounded-t bg-cyan-300" style={{ height: `${Math.max(2, (day.opened / max) * 90)}%` }} />
                    <span className="rounded-t bg-pink-300" style={{ height: `${Math.max(2, (day.clicked / max) * 90)}%` }} />
                    <span className="rounded-t bg-green-300" style={{ height: `${Math.max(2, (day.converted / max) * 90)}%` }} />
                  </div>
                  <span className="text-[9px] text-white/30 text-center">{new Date(day.date).getDate()}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`rounded-xl p-4 ${isDarkMode ? "bg-black/20" : "bg-white/60"}`} data-testid="campaign-history-panel">
          <h4 className="font-semibold mb-3 text-sm">Campaign History</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {campaignHistory.length === 0 ? (
              <p className="text-xs text-white/40" data-testid="campaign-history-empty">No campaign runs yet</p>
            ) : campaignHistory.slice(0, 6).map((campaign) => (
              <div key={campaign.id || campaign.run_at} className="flex items-center justify-between rounded-lg bg-white/5 p-3" data-testid={`campaign-history-row-${campaign.id || campaign.run_at}`}>
                <div>
                  <p className="text-sm font-medium">{campaign.type || "winback"}</p>
                  <p className="text-xs text-white/40">{formatDate(campaign.run_at)}</p>
                </div>
                <div className="text-right text-xs">
                  <p className="text-green-400">Sent: {campaign.sent_count || 0}</p>
                  <p className="text-white/40">Targets: {campaign.target_count || campaign.eligible_users || 0}</p>
                  {campaign.metrics && <p className="text-cyan-300" data-testid={`campaign-row-open-rate-${campaign.id || campaign.run_at}`}>Open: {campaign.metrics.open_rate}%</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`rounded-xl p-4 ${isDarkMode ? "bg-black/20" : "bg-white/60"}`} data-testid="suppression-list-panel">
          <h4 className="font-semibold mb-3 text-sm flex items-center gap-2">
            <ShieldOff className="w-4 h-4 text-amber-300" />
            Suppression List
          </h4>
          <div className="flex gap-2 mb-3">
            <Input value={suppressionEmail} onChange={(e) => setSuppressionEmail(e.target.value)} placeholder="email@example.com" className="bg-white/5 border-white/10" data-testid="suppression-email-input" />
            <Button onClick={addSuppression} className="bg-amber-400 text-black hover:bg-amber-300" data-testid="add-suppression-button">Suppress</Button>
          </div>
          <div className="space-y-2 max-h-36 overflow-y-auto">
            {suppressionEntries.length === 0 ? (
              <p className="text-xs text-white/40" data-testid="suppression-list-empty">No suppressed emails</p>
            ) : suppressionEntries.map((entry) => (
              <div key={entry.email} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2" data-testid={`suppression-row-${entry.email}`}>
                <div className="min-w-0">
                  <p className="text-sm truncate">{entry.email}</p>
                  <p className="text-[10px] text-white/35">{entry.reason || "suppressed"}</p>
                </div>
                <button onClick={() => removeSuppression(entry.email)} className="p-1.5 rounded-lg hover:bg-red-500/20" data-testid={`remove-suppression-${entry.email}`}>
                  <Trash2 className="w-4 h-4 text-red-300" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {campaignMetrics && (
        <p className="mt-3 text-xs text-white/35 flex items-center gap-1" data-testid="campaign-deliverability-summary">
          <Percent className="w-3 h-3" /> {campaignMetrics.suppressed} suppressed • {campaignMetrics.winback_optouts} win-back category opt-outs
        </p>
      )}
    </motion.div>
  );
}